import React from 'react';
import Media from 'react-media';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IReduxRootState } from '../app/reduxRootState.interface';
import Icon from '../app/common/components/Icon';
import ICONS from '../app/common/constants/icons';
import { COLORS, FONT_FAMILY, MQS, SIZES } from '../app/common/constants/styles';
import Logo from '../app/common/logos/RakutenLogo';
import { IClientConfig, IPresentationConfig } from '../config/clientConfig';
import SignOutLinkContainer from '../signOut/SignOutLinkContainer';
import * as webStorage from '@com.rakuten.esd/myr-web-storage';

const Base = styled.header`
    height: 60px;
    background-color: ${COLORS.white};
`;

const HeaderWrapper = styled.div`
    align-items: center;
    display: flex;
    height: 100%;
    margin: auto;
    max-width: ${1.1 * SIZES.appMaxWidth}px;
    padding: 0 20px;
    position: relative;
`;

const HeaderLeft = styled.div`
    bottom: 0;
    height: 37px;
    margin: auto;
    position: absolute;
    top: 0;
`;

const HeaderSpacer = styled.div`
    flex: 1 1 0px;
`;

const HeaderRight = styled.div`
    align-items: center;
    display: flex;
`;

const UserEmail = styled.div`
    color: #000;
    font-size: 0.875rem;
    ${FONT_FAMILY.multipleBoldCss};
    font-weight: 500;
    text-decoration: none;
    text-transform: uppercase;
    margin-top: 7px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const UserEmailLink = styled(Link)`
    color: #000;
    font-size: 0.875rem;
    font-weight: 500;
    text-decoration: none;
    text-transform: uppercase;
`;

const LogoWrapper = styled.div`
    height: 100%;

    a {
        cursor: pointer;
        display: block;
        height: 100%;
    }
`;
const ImgLogo = styled.img`
    max-width: 100%;
`;
const Points = styled(Link)`
    margin-left: 25px;
    font-weight: 500;
    text-decoration: none;
    color: #000;

    & span {
        margin-right: 5px;
    }

    & svg {
        transform: translateY(5px);
    }
`;

interface IDefaultHeaderProps {
    emailClickUrl: string;
    returnUrl: string;
    serviceLogoURL: string;
    serviceSPLogoURL: string;
    fullName: string;
    memberInfo: any;
    clientConfig: IClientConfig;
    presentationConfig: IPresentationConfig;
    displayUsername: string;
}

const DefaultHeader = ({
    emailClickUrl,
    returnUrl,
    serviceLogoURL,
    serviceSPLogoURL,
    fullName,
    memberInfo,
    displayUsername,
    presentationConfig: {
        showSections,
        header: { showSignOut },
    },
}: IDefaultHeaderProps) => {
    const pointsComponent =
        showSections && showSections.showPoints ? (
            <Points key="points" to="/points-club" data-qa-id="header-points">
                <span data-qa-id="header-points-amount">{memberInfo.superPointsBalance}</span>
                <Icon icon={ICONS.POINTS} size={'20'} />
            </Points>
        ) : null;

    const emailComponent = showSignOut ? (
        <SignOutLinkContainer>
            <UserEmail data-qa-id="header-email">{memberInfo.email}</UserEmail>
        </SignOutLinkContainer>
    ) : (
        <UserEmail key="userEmail" data-qa-id="header-email" className="notranslate">
            {displayUsername}
        </UserEmail>
    );

    const rightSideContent = (
        <React.Fragment>
            {emailComponent}
            {pointsComponent}
        </React.Fragment>
    );

    const logo = serviceLogoURL ? <ImgLogo src={serviceLogoURL} alt={fullName} /> : <Logo />;
    const splogo = serviceSPLogoURL ? <ImgLogo src={serviceSPLogoURL} alt={fullName} /> : logo;

    return (
        <Base>
            <HeaderWrapper>
                <HeaderLeft>
                    <LogoWrapper>
                        <a
                            href={returnUrl}
                            tabIndex={0}
                            onClick={() => {
                                webStorage.removeState();
                            }}
                        >
                            <Media query={`(min-width: ${MQS.mq2Min})`}>
                                {(matches) => (matches ? logo : splogo)}
                            </Media>
                        </a>
                    </LogoWrapper>
                </HeaderLeft>
                <HeaderSpacer />
                <HeaderRight>
                    <Media query={`(min-width: ${MQS.mq2Min})`} children={rightSideContent} />
                </HeaderRight>
            </HeaderWrapper>
        </Base>
    );
};

const mapStateToProps = (state: IReduxRootState) => {
    const memberInfo = state.member;

    const currentLanguage = state.app.locale;
    const reverse =
        currentLanguage === 'ja' || currentLanguage === 'zh-CN' || currentLanguage === 'zh-TW';
    const displayUsername =
        memberInfo.firstName && memberInfo.lastName
            ? reverse
                ? memberInfo.lastName + ' ' + memberInfo.firstName
                : memberInfo.firstName + ' ' + memberInfo.lastName
            : memberInfo.email;

    return {
        appConfig: state.app.appConfig,
        clientConfig: state.app.clientConfig,
        presentationConfig: state.app.presentationConfig,
        emailClickUrl: state.app.presentationConfig.header.userIDUrl,
        memberInfo: state.member,
        returnUrl: state.app ? state.app.clientConfig.returnUrl : '/',
        serviceLogoURL: state.app.clientConfig.serviceLogoURL
            ? state.app.clientConfig.serviceLogoURL
            : '',
        serviceSPLogoURL: state.app.clientConfig.serviceSPLogoURL
            ? state.app.clientConfig.serviceSPLogoURL
            : '',
        fullName: state.app.clientConfig.fullName ? state.app.clientConfig.fullName : '',
        displayUsername,
    };
};

export default connect(mapStateToProps)(DefaultHeader);
